import React, { useState, useCallback, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faCreditCard,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faSync,
  faReceipt,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import AdminDashboardAPI from "../../services/AdminDashboardAPI";
import "./css/UserSubscriptionAudit.css";

/**
 * Platform Admin tool: given a user UUID, show a chronological visual timeline
 * (default 6 months) of every subscription change AND every payment event,
 * along with the user's current subscription status. Used by support staff
 * to troubleshoot user calls about subscriptions/billing.
 */

const ACTION_LABELS = {
  CREATE: "Subscription Created",
  PLAN_CHANGE: "Plan Changed",
  CANCEL: "Cancelled",
  UNDO_CANCEL: "Cancellation Reversed",
  DOWNGRADE_SCHEDULED: "Downgrade Scheduled",
  DOWNGRADE_CANCELLED: "Downgrade Cancelled",
  BILLING_CYCLE_CHANGE: "Billing Cycle Changed",
  PROMO_APPLIED: "Promo Applied",
  STATUS_CHANGE: "Status Changed",
};

const ACTION_ICONS = {
  CREATE: "🆕",
  PLAN_CHANGE: "🔄",
  CANCEL: "🚫",
  UNDO_CANCEL: "↩️",
  DOWNGRADE_SCHEDULED: "⏬",
  DOWNGRADE_CANCELLED: "❌",
  BILLING_CYCLE_CHANGE: "🔁",
  PROMO_APPLIED: "🎟️",
  STATUS_CHANGE: "📋",
};

const PAYMENT_STATUS_ICONS = {
  SUCCEEDED: faCheckCircle,
  FAILED: faTimesCircle,
  REFUNDED: faSync,
  PENDING: faClock,
  PROCESSING: faClock,
  CANCELED: faTimesCircle,
};

// Basic UUID v1-v5 shape check (matches backend UserUtil.isValidUuidFormat)
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    // Date-only values (e.g. LocalDate "2026-05-31" from the API) are parsed by JS as
    // UTC midnight; rendering them in the viewer's local timezone shifts them back a day
    // (west of UTC). Pin date-only values to UTC so the calendar date is preserved.
    const isDateOnly =
      typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...(isDateOnly ? { timeZone: "UTC" } : {}),
    });
  } catch {
    return String(value);
  }
};

const formatPlanName = (name) => {
  if (!name) return "—";
  return String(name)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatMoney = (amount, currency = "USD") => {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const formatAmountWithCycle = (amount, cycle) => {
  if (amount == null) return null;
  if (Number(amount) === 0) return "Free";
  const label =
    cycle && cycle.toUpperCase() === "YEARLY"
      ? "yr"
      : cycle && cycle.toUpperCase() === "MONTHLY"
        ? "mo"
        : null;
  const money = formatMoney(amount);
  return label ? `${money}/${label}` : money;
};

function CurrentSubscriptionCard({ subscription }) {
  if (!subscription) {
    return (
      <div className="user-sub-audit__current user-sub-audit__current--empty">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <span>No subscription on record for this user.</span>
      </div>
    );
  }

  const statusClass = `user-sub-audit__status-pill ${(subscription.status || "")
    .toString()
    .toLowerCase()}`;

  return (
    <div className="user-sub-audit__current">
      <div className="user-sub-audit__current-row">
        <div className="user-sub-audit__current-plan">
          <FontAwesomeIcon icon={faCreditCard} />
          <span className="user-sub-audit__current-plan-name">
            {formatPlanName(subscription.planName)}
          </span>
          <span className={statusClass}>{subscription.status || "—"}</span>
          {subscription.isTrial ? (
            <span className="user-sub-audit__trial-pill">TRIAL</span>
          ) : null}
        </div>
        <div className="user-sub-audit__current-amount">
          {formatAmountWithCycle(
            subscription.amount,
            subscription.paymentCycle,
          )}
        </div>
      </div>
      <div className="user-sub-audit__current-grid">
        <div>
          <span className="user-sub-audit__label">Start</span>
          <span>{formatDate(subscription.startDate)}</span>
        </div>
        <div>
          <span className="user-sub-audit__label">Next billing</span>
          <span>{formatDate(subscription.nextBillingDate)}</span>
        </div>
        <div>
          <span className="user-sub-audit__label">Trial ends</span>
          <span>{formatDate(subscription.trialEndDate)}</span>
        </div>
        <div>
          <span className="user-sub-audit__label">Grace ends</span>
          <span>{formatDate(subscription.graceEndDate)}</span>
        </div>
        <div>
          <span className="user-sub-audit__label">Auto renew</span>
          <span>{subscription.autoRenew ? "Yes" : "No"}</span>
        </div>
        <div>
          <span className="user-sub-audit__label">Cancelled at</span>
          <span>{formatDateTime(subscription.cancelledAt)}</span>
        </div>
        {subscription.pendingPlanId ? (
          <div className="user-sub-audit__pending-cell">
            <span className="user-sub-audit__label">Pending change</span>
            <span>
              → plan #{subscription.pendingPlanId}
              {subscription.pendingPlanChangeDate
                ? ` on ${formatDate(subscription.pendingPlanChangeDate)}`
                : ""}
            </span>
          </div>
        ) : null}
        {subscription.promoCode ? (
          <div>
            <span className="user-sub-audit__label">Promo code</span>
            <span>{subscription.promoCode}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SubscriptionEvent({ event }) {
  const icon = ACTION_ICONS[event.action] || "📝";
  const label = ACTION_LABELS[event.action] || event.action;
  const prevPrice = formatAmountWithCycle(
    event.previousAmount,
    event.previousCycle,
  );
  const newPrice = formatAmountWithCycle(event.newAmount, event.newCycle);

  return (
    <div
      className={`user-sub-audit__entry user-sub-audit__entry--sub user-sub-audit__entry--${(
        event.action || ""
      )
        .toString()
        .toLowerCase()}`}
    >
      <div className="user-sub-audit__entry-dot">{icon}</div>
      <div className="user-sub-audit__entry-content">
        <div className="user-sub-audit__entry-header">
          <span className="user-sub-audit__entry-action">{label}</span>
          <span className="user-sub-audit__entry-date">
            {formatDateTime(event.timestamp)}
          </span>
        </div>

        {(event.previousPlanName || event.newPlanName) && (
          <div className="user-sub-audit__entry-row">
            {event.previousPlanName && (
              <span className="user-sub-audit__plan-badge previous">
                {formatPlanName(event.previousPlanName)}
                {prevPrice && ` (${prevPrice})`}
              </span>
            )}
            {event.previousPlanName && event.newPlanName && (
              <span className="user-sub-audit__arrow">→</span>
            )}
            {event.newPlanName && (
              <span className="user-sub-audit__plan-badge new">
                {formatPlanName(event.newPlanName)}
                {newPrice && ` (${newPrice})`}
              </span>
            )}
          </div>
        )}

        {(event.previousStatus || event.newStatus) && (
          <div className="user-sub-audit__entry-row">
            {event.previousStatus && (
              <span
                className={`user-sub-audit__status-badge ${event.previousStatus
                  .toString()
                  .toLowerCase()}`}
              >
                {event.previousStatus}
              </span>
            )}
            {event.previousStatus && event.newStatus && (
              <span className="user-sub-audit__arrow">→</span>
            )}
            {event.newStatus && (
              <span
                className={`user-sub-audit__status-badge ${event.newStatus
                  .toString()
                  .toLowerCase()}`}
              >
                {event.newStatus}
              </span>
            )}
          </div>
        )}

        {event.details && (
          <p className="user-sub-audit__entry-details">{event.details}</p>
        )}
      </div>
    </div>
  );
}

function PaymentEvent({ event }) {
  const status = (event.status || "").toString().toUpperCase();
  const statusIcon = PAYMENT_STATUS_ICONS[status] || faReceipt;
  const card =
    event.cardBrand && event.cardLast4
      ? `${formatPlanName(event.cardBrand)} •••• ${event.cardLast4}`
      : event.paymentMethod || "Payment";

  return (
    <div
      className={`user-sub-audit__entry user-sub-audit__entry--payment user-sub-audit__entry--payment-${status.toLowerCase()}`}
    >
      <div className="user-sub-audit__entry-dot">
        <FontAwesomeIcon icon={statusIcon} />
      </div>
      <div className="user-sub-audit__entry-content">
        <div className="user-sub-audit__entry-header">
          <span className="user-sub-audit__entry-action">
            Payment {status ? `· ${status}` : ""} ·{" "}
            {formatMoney(event.amount, event.currency)}
          </span>
          <span className="user-sub-audit__entry-date">
            {formatDateTime(event.timestamp)}
          </span>
        </div>
        <div className="user-sub-audit__entry-row user-sub-audit__entry-meta">
          <span>{card}</span>
          {event.stripeInvoiceId && (
            <span className="user-sub-audit__mono">
              invoice: {event.stripeInvoiceId}
            </span>
          )}
          {event.stripePaymentIntentId && (
            <span className="user-sub-audit__mono">
              pi: {event.stripePaymentIntentId}
            </span>
          )}
        </div>
        {event.failureReason && (
          <p className="user-sub-audit__entry-details user-sub-audit__entry-details--error">
            <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
            {event.failureReason}
          </p>
        )}
        {event.description && (
          <p className="user-sub-audit__entry-details">{event.description}</p>
        )}
      </div>
    </div>
  );
}

function UserSubscriptionAudit({ initialUuid } = {}) {
  const [uuidInput, setUuidInput] = useState(initialUuid || "");
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileMsg, setReconcileMsg] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [searchedUuid, setSearchedUuid] = useState("");

  // Stripe price update
  const [priceIdInput, setPriceIdInput] = useState("");
  const [noProrate, setNoProrate] = useState(false);
  const [priceUpdateConfirm, setPriceUpdateConfirm] = useState(false);
  const [priceUpdating, setPriceUpdating] = useState(false);
  const [priceUpdateMsg, setPriceUpdateMsg] = useState(null);

  const runAudit = useCallback(async (uuid, monthsBack) => {
    const cleaned = (uuid || "").trim();
    if (!cleaned) {
      setError("Please enter a user UUID.");
      return;
    }
    if (!UUID_REGEX.test(cleaned)) {
      setError("That doesn't look like a valid UUID.");
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    setReconcileMsg(null);
    setSearchedUuid(cleaned);
    try {
      const response = await AdminDashboardAPI.getUserSubscriptionAudit(
        cleaned,
        monthsBack,
      );
      if (response && response.success === false) {
        setError(response.message || "Failed to load audit.");
        setData(null);
      } else {
        setData(response);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError("No user found with that UUID.");
      } else if (status === 403) {
        setError("You do not have permission to view this data.");
      } else {
        setError("Unable to load subscription audit. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run the audit when an initialUuid is supplied via deep link
  // (e.g. clicking "View Subscription Audit" from the user details panel).
  const autoRanForUuidRef = useRef(null);
  useEffect(() => {
    const cleaned = (initialUuid || "").trim();
    if (!cleaned) return;
    if (autoRanForUuidRef.current === cleaned) return;
    autoRanForUuidRef.current = cleaned;
    setUuidInput(cleaned);
    runAudit(cleaned, months);
    // months intentionally omitted: we only auto-run once per incoming UUID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUuid, runAudit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    runAudit(uuidInput, months);
  };

  const handleMonthsChange = (e) => {
    const next = Number(e.target.value);
    setMonths(next);
    if (data && searchedUuid) {
      runAudit(searchedUuid, next);
    }
  };

  const handlePriceUpdateSubmit = (e) => {
    e.preventDefault();
    const trimmed = priceIdInput.trim();
    if (!trimmed) {
      setPriceUpdateMsg({
        type: "error",
        text: "Please enter a Stripe Price ID.",
      });
      return;
    }
    if (!trimmed.startsWith("price_")) {
      setPriceUpdateMsg({
        type: "error",
        text: "Invalid Price ID — must start with 'price_'.",
      });
      return;
    }
    setPriceUpdateMsg(null);
    setPriceUpdateConfirm(true);
  };

  const handlePriceUpdateConfirm = useCallback(async () => {
    if (!searchedUuid || priceUpdating) return;
    setPriceUpdating(true);
    setPriceUpdateMsg(null);
    try {
      const result = await AdminDashboardAPI.updateUserStripePriceId(
        searchedUuid,
        priceIdInput.trim(),
        noProrate,
      );
      if (result && result.success) {
        setPriceUpdateMsg({
          type: "ok",
          text:
            result.message ||
            `Stripe subscription ${
              result.stripeSubscriptionId
            } updated to price ${result.newPriceId}.`,
        });
        setPriceIdInput("");
      } else {
        setPriceUpdateMsg({
          type: "error",
          text: (result && result.message) || "Update failed.",
        });
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      setPriceUpdateMsg({
        type: "error",
        text: apiMsg || "Unable to update Stripe price. Please try again.",
      });
    } finally {
      setPriceUpdating(false);
      setPriceUpdateConfirm(false);
    }
  }, [searchedUuid, priceIdInput, noProrate, priceUpdating]);

  const handleReconcile = useCallback(async () => {
    if (!searchedUuid || reconciling) return;
    setReconciling(true);
    setReconcileMsg(null);
    try {
      const result =
        await AdminDashboardAPI.reconcileUserPayments(searchedUuid);
      if (result && result.success) {
        setReconcileMsg({
          type: result.created > 0 ? "ok" : "info",
          text:
            result.message ||
            (result.created > 0
              ? `Backfilled ${result.created} payment record(s) from Stripe.`
              : "No missing payments — already in sync with Stripe."),
        });
        // Refresh the audit so the new rows show up immediately.
        if (result.created > 0) {
          await runAudit(searchedUuid, months);
        }
      } else {
        setReconcileMsg({
          type: "error",
          text: (result && result.message) || "Reconcile failed.",
        });
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      setReconcileMsg({
        type: "error",
        text: apiMsg || "Unable to reconcile payments. Please try again.",
      });
    } finally {
      setReconciling(false);
    }
  }, [searchedUuid, reconciling, months, runAudit]);

  return (
    <div className="user-sub-audit">
      <header className="user-sub-audit__header">
        <h2>
          <FontAwesomeIcon icon={faClipboardList} /> User Subscription Audit
        </h2>
        <p className="user-sub-audit__subtitle">
          Look up a user by UUID to see a visual timeline of their subscription
          changes and payment activity. Use this to help users when they call in
          with questions about their subscription or billing.
        </p>
      </header>

      <form className="user-sub-audit__form" onSubmit={handleSubmit}>
        <label htmlFor="user-sub-audit-uuid" className="user-sub-audit__label">
          User UUID
        </label>
        <div className="user-sub-audit__form-row">
          <input
            id="user-sub-audit-uuid"
            type="text"
            className="user-sub-audit__input"
            placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
            value={uuidInput}
            onChange={(e) => setUuidInput(e.target.value)}
            spellCheck={false}
            autoComplete="off"
          />
          <select
            className="user-sub-audit__select"
            value={months}
            onChange={handleMonthsChange}
            aria-label="History window"
          >
            <option value={1}>Last 1 month</option>
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
          <button
            type="submit"
            className="user-sub-audit__btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Searching…
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSearch} /> Search
              </>
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="user-sub-audit__error">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}

      {loading && !data && (
        <div className="user-sub-audit__loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading audit timeline…
        </div>
      )}

      {data && (
        <>
          <section className="user-sub-audit__section">
            <div className="user-sub-audit__section-head">
              <h3 className="user-sub-audit__section-title">
                Current Subscription
              </h3>
              <button
                type="button"
                className="user-sub-audit__reconcile-btn"
                onClick={handleReconcile}
                disabled={reconciling || loading}
                title="Pull paid invoices from Stripe and backfill any missing local payment records (e.g. from missed webhooks)."
              >
                {reconciling ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Reconciling…
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} /> Reconcile from Stripe
                  </>
                )}
              </button>
            </div>
            {reconcileMsg && (
              <div
                className={`user-sub-audit__reconcile-msg user-sub-audit__reconcile-msg--${reconcileMsg.type}`}
              >
                {reconcileMsg.text}
              </div>
            )}
            <CurrentSubscriptionCard subscription={data.currentSubscription} />
            <div className="user-sub-audit__ids">
              <span>
                <strong>UUID:</strong>{" "}
                <span className="user-sub-audit__mono">{data.userUuid}</span>
              </span>
              {data.accountId != null && (
                <span>
                  <strong>Account ID:</strong> {data.accountId}
                </span>
              )}
              {data.userId != null && (
                <span>
                  <strong>User ID:</strong> {data.userId}
                </span>
              )}
            </div>
          </section>

          {data.summary && (
            <section className="user-sub-audit__summary">
              <div className="user-sub-audit__stat">
                <span className="user-sub-audit__stat-num">
                  {data.summary.totalSubscriptionChanges}
                </span>
                <span className="user-sub-audit__stat-label">Sub changes</span>
              </div>
              <div className="user-sub-audit__stat">
                <span className="user-sub-audit__stat-num">
                  {data.summary.totalPayments}
                </span>
                <span className="user-sub-audit__stat-label">Payments</span>
              </div>
              <div className="user-sub-audit__stat user-sub-audit__stat--ok">
                <span className="user-sub-audit__stat-num">
                  {data.summary.successfulPayments}
                </span>
                <span className="user-sub-audit__stat-label">Succeeded</span>
              </div>
              <div className="user-sub-audit__stat user-sub-audit__stat--bad">
                <span className="user-sub-audit__stat-num">
                  {data.summary.failedPayments}
                </span>
                <span className="user-sub-audit__stat-label">Failed</span>
              </div>
              <div className="user-sub-audit__stat">
                <span className="user-sub-audit__stat-num">
                  {formatMoney(data.summary.totalPaidAmount)}
                </span>
                <span className="user-sub-audit__stat-label">Total paid</span>
              </div>
            </section>
          )}

          <section className="user-sub-audit__section user-sub-audit__price-section">
            <h3 className="user-sub-audit__section-title">
              Update Stripe Price ID
            </h3>
            <p className="user-sub-audit__price-desc">
              Swap the Stripe Price object on this user&apos;s active
              subscription. Stripe will immediately generate a prorated invoice
              for any difference. Create the new Price in the Stripe Dashboard
              first, then paste its ID below.
            </p>
            <form
              className="user-sub-audit__price-form"
              onSubmit={handlePriceUpdateSubmit}
            >
              <input
                type="text"
                className="user-sub-audit__input user-sub-audit__price-input"
                placeholder="price_xxxxxxxxxxxxxxxxxxxxxxxx"
                value={priceIdInput}
                onChange={(e) => {
                  setPriceIdInput(e.target.value);
                  setPriceUpdateMsg(null);
                  setPriceUpdateConfirm(false);
                }}
                spellCheck={false}
                autoComplete="off"
                disabled={priceUpdating}
              />
              <label className="user-sub-audit__price-noprorate">
                <input
                  type="checkbox"
                  checked={noProrate}
                  onChange={(e) => setNoProrate(e.target.checked)}
                  disabled={priceUpdating}
                />
                No prorate — apply at next renewal only
              </label>
              <button
                type="submit"
                className="user-sub-audit__btn user-sub-audit__price-btn"
                disabled={priceUpdating || !priceIdInput.trim()}
              >
                Review
              </button>
            </form>

            {priceUpdateConfirm && !priceUpdating && (
              <div className="user-sub-audit__price-confirm">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>
                  Apply{" "}
                  <span className="user-sub-audit__mono">
                    {priceIdInput.trim()}
                  </span>{" "}
                  to <strong>{searchedUuid}</strong>?{" "}
                  {noProrate
                    ? "Price change will take effect at next renewal (no proration)."
                    : "Stripe will prorate and invoice immediately."}
                </span>
                <button
                  type="button"
                  className="user-sub-audit__btn user-sub-audit__price-btn--danger"
                  onClick={handlePriceUpdateConfirm}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="user-sub-audit__reconcile-btn"
                  onClick={() => setPriceUpdateConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}

            {priceUpdating && (
              <div className="user-sub-audit__price-confirm">
                <FontAwesomeIcon icon={faSpinner} spin /> Updating Stripe
                subscription…
              </div>
            )}

            {priceUpdateMsg && (
              <div
                className={`user-sub-audit__reconcile-msg user-sub-audit__reconcile-msg--${priceUpdateMsg.type}`}
              >
                {priceUpdateMsg.text}
              </div>
            )}
          </section>

          <section className="user-sub-audit__section">
            <h3 className="user-sub-audit__section-title">
              Activity Timeline · Last {data.monthsBack} month
              {data.monthsBack === 1 ? "" : "s"}
            </h3>
            <p className="user-sub-audit__window">
              {formatDate(data.windowStart)} — {formatDate(data.windowEnd)}
            </p>

            {(!data.events || data.events.length === 0) && (
              <div className="user-sub-audit__empty">
                <div className="user-sub-audit__empty-icon">📭</div>
                <p>
                  No subscription or payment activity in the selected window.
                </p>
              </div>
            )}

            {data.events && data.events.length > 0 && (
              <div className="user-sub-audit__timeline">
                {data.events.map((event, idx) => {
                  const key =
                    event.type === "PAYMENT"
                      ? `pay-${event.paymentId ?? idx}`
                      : `sub-${event.auditId ?? idx}`;
                  return event.type === "PAYMENT" ? (
                    <PaymentEvent key={key} event={event} />
                  ) : (
                    <SubscriptionEvent key={key} event={event} />
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default UserSubscriptionAudit;
